import React, { useState } from 'react';
import { useAppDispatch } from 'hooks';
import { Progress } from 'reactstrap';

import { apiDocumentBatchesApi } from '~constants/api';

import { getStreamableZipDownload } from '~libs/downloadUtils';
import { download as downloadFile } from '~libs/genericAction';
import { useToast } from '~libs/toast';

import { ClickableIcon } from '~components';
import { faArrowRotateBack, faCancel, faDownload } from '~components/icon';
import { Row } from '~components/table-view';

import {
  documentBatchesApi,
  useCancelDocumentBatchMutation,
  useRetryDocumentBatchMutation,
} from '~main/actions/documentBatches';

interface ComponentProps {
  row: Row;
}

const ActionButtons: React.FC<ComponentProps> = ({ row }) => {
  const dispatch = useAppDispatch();
  const { id, status, reference } = row || {};

  const [retryBatch, { isLoading: isRetrying }] = useRetryDocumentBatchMutation();
  const [cancelBatch, { isLoading: isCancelling }] = useCancelDocumentBatchMutation();

  const canDownload = status?.tag === 'completed';
  const canRetry = status?.tag === 'failed' || status?.tag === 'cancelled';
  const canCancel = status?.tag === 'queued' || status?.tag === 'processing';
  const showDownload = status?.tag === 'completed' || status?.tag === 'queued' || status?.tag === 'processing';

  // Progress indicator state
  const [downloading, setDownloading] = useState(false);
  const { showToast } = useToast();

  const download = async (id: string | number) => {
    setDownloading(true);
    showToast('info', 'Download started');
    const filename = `${reference || `batch_${id}`}.zip`;
    try {
      const data = await downloadFile(`${apiDocumentBatchesApi}/${id}/download`);
      const response = await getStreamableZipDownload({ blob: data.data, filename });

      if (response) {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(response.data);
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(link.href);

        showToast('success', `Download complete: ${filename}`);
        // Invalidate cache to refresh the table with updated downloaded_at
        dispatch(documentBatchesApi.util.invalidateTags([{ type: 'documentBatches', id: 'LIST' }]));
      } else {
        showToast('danger', 'Download failed: Invalid ZIP content');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Download failed';
      showToast('danger', message);
      console.error('Failed to download batch:', error);
    } finally {
      setDownloading(false);
    }
  };

  const retry = async (id: string | number) => {
    showToast('info', `Retrying batch ${reference || id}…`);
    try {
      const result = await retryBatch(id as string).unwrap();
      if (result) {
        showToast('success', `Retry queued for batch ${reference || id}`);
        // Ensure list refresh in case cache is stale
        dispatch(documentBatchesApi.util.invalidateTags([{ type: 'documentBatches', id: 'LIST' }]));
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Retry failed';
      showToast('danger', `Retry failed for batch ${reference || id}: ${message}`);
      console.error('Failed to retry batch:', error);
    }
  };

  const cancel = async (id: string | number) => {
    showToast('info', `Cancelling batch ${reference || id}…`);
    try {
      const result = await cancelBatch(id as string).unwrap();
      if (result) {
        showToast('success', `Cancelled batch ${reference || id}`);
        // Ensure list refresh in case cache is stale
        dispatch(documentBatchesApi.util.invalidateTags([{ type: 'documentBatches', id: 'LIST' }]));
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Cancel failed';
      showToast('danger', `Cancel failed for batch ${reference || id}: ${message}`);
      console.error('Failed to cancel batch:', error);
    }
  };

  return (
    <div className="d-flex justify-content-end align-items-center gap-2 w-100">
      {downloading ? (
        <Progress className="w-100" style={{ height: '20px' }} animated value={100}>
          Downloading…
        </Progress>
      ) : (
        <>
          {showDownload && (
            <ClickableIcon
              id={`${id}-download`}
              icon={faDownload}
              onClick={() => download(id)}
              toolTip="Download"
              disabled={!canDownload}
              color="success"
            />
          )}
          {canRetry && (
            <ClickableIcon
              id={`${id}-retry`}
              icon={faArrowRotateBack}
              onClick={() => retry(id)}
              toolTip="Retry"
              disabled={isRetrying}
            />
          )}
          {canCancel && (
            <ClickableIcon
              id={`${id}-cancel`}
              icon={faCancel}
              onClick={() => cancel(id)}
              toolTip="Cancel"
              color="danger"
              disabled={isCancelling}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ActionButtons;
